import { PATH_ADMIN } from '../../routes/paths';

// Everything the Ctrl+K palette needs to know about a result type: how it
// looks and where it opens. The backend (accounts/cadmin/search_views.py)
// decides WHAT is found and who may see it; this file only decides how a hit
// is drawn and which page it lands on.

export const TYPE_META = {
  member: { label: 'Member', icon: 'solar:user-rounded-bold-duotone', color: 'primary' },
  company: { label: 'Company', icon: 'solar:buildings-2-bold-duotone', color: 'info' },
  lead: { label: 'Lead', icon: 'solar:target-bold-duotone', color: 'warning' },
  invoice: { label: 'Invoice', icon: 'solar:bill-list-bold-duotone', color: 'success' },
  subscription: { label: 'Subscription', icon: 'solar:card-recive-bold-duotone', color: 'secondary' },
  meeting_room: { label: 'Meeting room', icon: 'solar:videocamera-record-bold-duotone', color: 'info' },
  booking: { label: 'Booking', icon: 'solar:calendar-mark-bold-duotone', color: 'secondary' },
  ticket: { label: 'Ticket', icon: 'solar:ticket-bold-duotone', color: 'error' },
  work_order: { label: 'Work order', icon: 'solar:clipboard-list-bold-duotone', color: 'error' },
  visitor: { label: 'Visitor', icon: 'solar:users-group-two-rounded-bold-duotone', color: 'primary' },
  proposal: { label: 'Proposal', icon: 'solar:document-text-bold-duotone', color: 'warning' },
  space: { label: 'Space', icon: 'solar:widget-5-bold-duotone', color: 'info' },
  page: { label: 'Page', icon: 'solar:arrow-right-up-linear', color: 'grey' },
  action: { label: 'Action', icon: 'solar:bolt-bold-duotone', color: 'primary' },
  campus: { label: 'Campus', icon: 'solar:map-point-bold-duotone', color: 'primary' },
  dex: { label: 'Dex', icon: 'solar:magic-stick-3-bold-duotone', color: 'secondary' },
};

// Filter chips, in the order the backend returns its sections.
export const FILTERS = [
  { key: 'members', label: 'Members' },
  { key: 'companies', label: 'Companies' },
  { key: 'leads', label: 'Leads' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'meeting_rooms', label: 'Rooms' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'visitors', label: 'Visitors' },
  { key: 'proposals', label: 'Proposals' },
  { key: 'spaces', label: 'Spaces' },
];

/** Where a record opens. Returns { path, state? } or null. */
export function resultTarget(item) {
  switch (item.type) {
    case 'member':
      return { path: `${PATH_ADMIN.members.root}/${item.id}` };
    case 'company':
      return { path: `${PATH_ADMIN.members.subscriptions}/${item.id}` };
    case 'lead':
      return { path: PATH_ADMIN.leadsManagement.leadDetail(item.id) };
    case 'invoice':
      return { path: `${PATH_ADMIN.orderManagement.invoiceList}?invoice=${item.id}` };
    case 'subscription':
      return { path: PATH_ADMIN.members.membership(item.id) };
    case 'meeting_room':
      return { path: PATH_ADMIN.meetingRoomManagement.calendar(item.id) };
    case 'booking':
      return {
        path: PATH_ADMIN.meetingRoomManagement.calendar(item.room_id),
        state: item.start ? { focusDate: item.start } : undefined,
      };
    case 'ticket':
      return { path: `${PATH_ADMIN.serviceRequests.tickets}?ticket=${item.id}&ticket_campus=${item.campus_id || ''}` };
    case 'work_order':
      return { path: PATH_ADMIN.serviceRequests.workOrder(item.id) };
    case 'visitor':
      return { path: `${PATH_ADMIN.visitorManagement}?tab=visitors` };
    case 'proposal':
      return {
        path: item.lead_id
          ? PATH_ADMIN.leadsManagement.leadDetail(item.lead_id)
          : PATH_ADMIN.leadsManagement.proposals,
      };
    case 'space':
      return { path: PATH_ADMIN.SpaceManagement };
    default:
      return item.path ? { path: item.path, state: item.state } : null;
  }
}

// Things you DO rather than find. Gated with the same shape the sidebar uses
// (isItemVisible), mirroring each target route's own RoleBasedGuard.
export const QUICK_ACTIONS = [
  {
    id: 'new-order',
    title: 'New order',
    subtitle: 'Create a subscription or lease',
    keywords: 'create subscription sell booking desk seat order',
    icon: 'solar:cart-plus-bold-duotone',
    path: PATH_ADMIN.orderManagement.createV2,
    gate: { roles: ['org'], permissions: ['billing.create', 'subscriptions.manage'] },
  },
  {
    id: 'add-member',
    title: 'Add member',
    subtitle: 'Onboard a new member',
    keywords: 'create member user onboard new',
    icon: 'solar:user-plus-rounded-bold-duotone',
    path: PATH_ADMIN.members.add,
    gate: { roles: ['org'], permission: 'members.create' },
  },
  {
    id: 'new-lead',
    title: 'Leads',
    subtitle: 'Open the leads list to add or work a lead',
    keywords: 'create lead enquiry prospect crm new',
    icon: 'solar:target-bold-duotone',
    path: PATH_ADMIN.leadsManagement.list,
    gate: { roles: ['org'], permission: 'leads.view', slug: 'leads-management' },
  },
  {
    id: 'new-proposal',
    title: 'New proposal',
    subtitle: 'Draft a proposal for a lead',
    keywords: 'create proposal quote quotation',
    icon: 'solar:document-add-bold-duotone',
    path: PATH_ADMIN.leadsManagement.createProposal,
    gate: { roles: ['org'], permission: 'leads.manage', slug: 'leads-management' },
  },
  {
    id: 'book-room',
    title: 'Book a meeting room',
    subtitle: 'Open room availability',
    keywords: 'meeting room book booking calendar conference',
    icon: 'solar:calendar-add-bold-duotone',
    path: PATH_ADMIN.meetingRoomManagement.root,
    gate: { roles: ['org'], permission: 'meeting_rooms.view', slug: 'meeting-room' },
  },
  {
    id: 'new-ticket',
    title: 'Raise a ticket',
    subtitle: 'Log a service request',
    keywords: 'create ticket helpdesk service request issue complaint',
    icon: 'solar:ticket-bold-duotone',
    path: PATH_ADMIN.serviceRequests.create,
    gate: { roles: ['org'], permission: 'service_requests.view', slug: 'helpdesk' },
  },
  {
    id: 'check-in',
    title: 'Check-in',
    subtitle: 'Front desk check-ins',
    keywords: 'check in attendance front desk',
    icon: 'solar:login-3-bold-duotone',
    path: PATH_ADMIN.checkIns,
    gate: { roles: ['org'], permission: 'checkins.view', slug: 'checkin-management' },
  },
  {
    id: 'invite-visitor',
    title: 'Visitors',
    subtitle: 'Invite or check in a visitor',
    keywords: 'visitor guest invite invitation reception',
    icon: 'solar:users-group-two-rounded-bold-duotone',
    path: `${PATH_ADMIN.visitorManagement}?tab=invitations`,
    gate: { roles: ['org'], permission: 'visitors.view' },
  },
];

export const DEX_GATE = { roles: ['org'], permission: 'dashboard.view', slug: 'dex', path: PATH_ADMIN.dex };

const QUESTION_START = /^(who|what|whats|what's|when|where|which|why|how|show|list|give|tell|find|compare|summari[sz]e|explain|is|are|do|does|can|should|top|total)\b/i;

/** A query that reads like a question should offer Dex first, not last. */
export const looksLikeQuestion = (q) => {
  const t = (q || '').trim();
  if (!t) return false;
  return t.endsWith('?') || (QUESTION_START.test(t) && t.split(/\s+/).length >= 3);
};

/**
 * Cheap fuzzy score for client-side lists (pages, actions, campuses).
 * 0 = no match. Exact > prefix > word prefix > substring > in-order letters.
 */
export function fuzzyScore(query, text, keywords = '') {
  const q = (query || '').trim().toLowerCase();
  const t = (text || '').toLowerCase();
  if (!q || !t) return 0;
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  const words = t.split(/[\s›/&-]+/);
  if (words.some((w) => w.startsWith(q))) return 65;
  if (t.includes(q)) return 50;
  const k = keywords.toLowerCase();
  const qWords = q.split(/\s+/);
  if (k && qWords.every((w) => k.includes(w) || t.includes(w))) return 40;
  // subsequence: "invl" -> "Invoice List"
  let i = 0;
  for (let j = 0; j < t.length && i < q.length; j += 1) if (t[j] === q[i]) i += 1;
  if (i === q.length && q.length >= 3) return 20;
  return 0;
}
